import { gql, useMutation } from "@apollo/client";
import { client } from "../apolloClient";

const AI_MODEL_PROVIDER_MUTATIONS = {
  ADD_AI_MODEL_PROVIDER: gql`
    mutation AddAiModelProvider($userId: Int!, $providers: [AiModelProviderInput!]!) {
      addAiModelProvider(user_id: $userId, providers: $providers) {
        ai_model_provider_id
        provider_name
        user_id
        base_url
        api_key
        is_enabled
        is_active
        created_on
        updated_on
      }
    }
  `,
  DELETE_AI_MODEL_PROVIDER: gql`
    mutation DeleteAiModelProvider($id: Int!, $userId: Int!) {
      deleteAiModelProvider(
        ai_model_provider_id: $id
        user_id: $userId
      ) {
        ai_model_provider_id
      }
    }
  `,
  UPDATE_AI_MODEL_PROVIDER_STATUS: gql`
    mutation UpdateAiModelProviderStatus(
      $id: Int!
      $userId: Int!
      $isEnabled: Boolean!
    ) {
      updateAiModelProviderStatus(
        ai_model_provider_id: $id
        user_id: $userId
        is_enabled: $isEnabled
      ) {
        ai_model_provider_id
        is_enabled
      }
    }
  `,
};

const AI_MODEL_PROVIDER_QUERIES = {
  LIST_AI_MODEL_PROVIDERS: gql`
    query ListAiModelProviders($userId: Int!) {
      listAiModelProviders(user_id: $userId) {
        ai_model_provider_id
        provider_name
        user_id
        base_url
        api_key
        is_enabled
        is_active
        created_on
        updated_on
      }
    }
  `,
  LIST_AI_MODELS: gql`
    query ListAiModels($userId: Int!) {
      listAiModels(user_id: $userId) {
        value
        label
        is_default
      }
    }
  `,
};

export const useAiModelProviders = () => {
  const [addAiModelProviderMutation, { loading, error }] = useMutation(
    AI_MODEL_PROVIDER_MUTATIONS.ADD_AI_MODEL_PROVIDER
  );
  const [deleteAiModelProviderMutation] = useMutation(
    AI_MODEL_PROVIDER_MUTATIONS.DELETE_AI_MODEL_PROVIDER
  );
  const [updateAiModelProviderStatusMutation] = useMutation(
    AI_MODEL_PROVIDER_MUTATIONS.UPDATE_AI_MODEL_PROVIDER_STATUS
  );

  /**
   * Persists an array of AI model providers.
   * Each provider should have: provider_name, base_url, api_key, is_enabled?, is_active?
   */
  const addAiModelProviders = async (providers, userId) => {
    try {
      const { data, errors } = await addAiModelProviderMutation({
        variables: { providers, userId: parseInt(userId, 10) },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.addAiModelProvider) {
        throw new Error("Failed to save AI model providers");
      }

      return {
        success: true,
        data: data.addAiModelProvider,
      };
    } catch (e) {
      // Surface GraphQL errors explicitly when available
      const graphQLError = e.graphQLErrors?.[0];
      if (graphQLError?.message) {
        return { success: false, error: graphQLError.message };
      }

      const networkError = e.networkError?.result?.errors?.[0];
      if (networkError?.message) {
        return { success: false, error: networkError.message };
      }

      return { success: false, error: e.message || "Unknown error" };
    }
  };

  const listAiModelProviders = async (userId) => {
    try {
      const { data } = await client.query({
        query: AI_MODEL_PROVIDER_QUERIES.LIST_AI_MODEL_PROVIDERS,
        variables: { userId: parseInt(userId, 10) },
        fetchPolicy: "network-only",
      });

      return {
        success: true,
        data: data?.listAiModelProviders || [],
      };
    } catch (e) {
      const graphQLError = e.graphQLErrors?.[0];
      if (graphQLError?.message) {
        return { success: false, error: graphQLError.message };
      }

      const networkError = e.networkError?.result?.errors?.[0];
      if (networkError?.message) {
        return { success: false, error: networkError.message };
      }

      return { success: false, error: e.message || "Unknown error" };
    }
  };

  return {
    addAiModelProviders,
    listAiModelProviders,
    listAiModels: async (userId) => {
      try {
        const { data } = await client.query({
          query: AI_MODEL_PROVIDER_QUERIES.LIST_AI_MODELS,
          variables: { userId: parseInt(userId, 10) },
          fetchPolicy: "network-only",
        });

        return {
          success: true,
          data: data?.listAiModels || [],
        };
      } catch (e) {
        const graphQLError = e.graphQLErrors?.[0];
        if (graphQLError?.message) {
          return { success: false, error: graphQLError.message };
        }

        const networkError = e.networkError?.result?.errors?.[0];
        if (networkError?.message) {
          return { success: false, error: networkError.message };
        }

        return { success: false, error: e.message || "Unknown error" };
      }
    },
    deleteAiModelProvider: async (id, userId) => {
      try {
        const { data, errors } = await deleteAiModelProviderMutation({
          variables: { id: parseInt(id, 10), userId: parseInt(userId, 10) },
        });

        if (errors?.length > 0) {
          throw new Error(errors[0].message);
        }

        if (!data?.deleteAiModelProvider) {
          throw new Error("Failed to delete AI model provider");
        }

        return { success: true };
      } catch (e) {
        const graphQLError = e.graphQLErrors?.[0];
        if (graphQLError?.message) {
          return { success: false, error: graphQLError.message };
        }

        const networkError = e.networkError?.result?.errors?.[0];
        if (networkError?.message) {
          return { success: false, error: networkError.message };
        }

        return { success: false, error: e.message || "Unknown error" };
      }
    },
    updateAiModelProviderStatus: async (id, userId, isEnabled) => {
      try {
        const { data, errors } = await updateAiModelProviderStatusMutation({
          variables: {
            id: parseInt(id, 10),
            userId: parseInt(userId, 10),
            isEnabled,
          },
        });

        if (errors?.length > 0) {
          throw new Error(errors[0].message);
        }

        if (!data?.updateAiModelProviderStatus) {
          throw new Error("Failed to update connection");
        }

        return { success: true };
      } catch (e) {
        const graphQLError = e.graphQLErrors?.[0];
        if (graphQLError?.message) {
          return { success: false, error: graphQLError.message };
        }

        const networkError = e.networkError?.result?.errors?.[0];
        if (networkError?.message) {
          return { success: false, error: networkError.message };
        }

        return { success: false, error: e.message || "Unknown error" };
      }
    },
    loading,
    error,
  };
};

